import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Printer, Receipt as ReceiptIcon, Info } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { EmployerTabs } from "../../components/employer/EmployerTabs";
import { downloadReceiptTxt, printReceipt, normalizeReceipt } from "../../lib/receipt";

export default function EmployerBilling() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deployed, setDeployed] = useState(true);

  useEffect(() => {
    api.get("/billing/transactions")
      .then(({ data }) => setTxs(Array.isArray(data) ? data.filter((x) => x.payment_status === "paid") : []))
      .catch((err) => { if (err?.response?.status === 404) setDeployed(false); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-1">{t("billing.title")}</h1>
      <p className="text-stone-sub mb-6">{t("billing.subtitle")}</p>
      <EmployerTabs />

      {!deployed && (
        <div className="flex items-start gap-3 bg-brand-soft border border-stone-border rounded-2xl p-4 mb-6" data-testid="billing-not-deployed">
          <Info className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <p className="text-sm text-stone-sub">{t("billing.notDeployed")}</p>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : txs.length === 0 ? (
        <div className="py-16 text-center text-stone-muted bg-white rounded-2xl border border-stone-border" data-testid="billing-empty">
          <ReceiptIcon className="w-10 h-10 mx-auto mb-3 text-stone-muted/60" />
          {t("billing.empty")}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-border shadow-sm overflow-hidden" data-testid="billing-list">
          {txs.map((tx, i) => {
            const r = normalizeReceipt(tx, user);
            return (
              <div key={tx.session_id || tx.id || i} className="flex items-center justify-between gap-3 p-4 border-b border-stone-border/60 last:border-0 flex-wrap" data-testid="billing-row">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-housing/15 flex items-center justify-center shrink-0"><ReceiptIcon className="w-5 h-5 text-housing" /></div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-stone-text capitalize">{r.plan} · ${r.amount.toFixed(2)} {r.currency}</div>
                    <div className="text-xs text-stone-muted">{t("billing.receipt")}{r.number} · {r.date.toLocaleDateString(lang === "en" ? "en-US" : "es-MX")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-housing/15 text-housing">{t("billing.paid")}</span>
                  <button data-testid="billing-download" onClick={() => downloadReceiptTxt(tx, user, lang)} className="p-2 rounded-lg border border-stone-border text-stone-sub hover:text-brand hover:border-brand transition-colors" title={t("billing.download")}><Download className="w-4 h-4" /></button>
                  <button data-testid="billing-print" onClick={() => printReceipt(tx, user, lang)} className="p-2 rounded-lg border border-stone-border text-stone-sub hover:text-brand hover:border-brand transition-colors" title={t("billing.print")}><Printer className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
